import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { EvaluationCriteriaService } from "../../services/EvaluationCriteriaService";
import {
  EvaluationCriteria,
  EvaluationType,
  EvaluationFramework,
} from "../../model_gen/evaluation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2Icon, PlusIcon, XIcon, GripVerticalIcon } from "lucide-react";
import { toast } from "sonner";

export type ScoringType = "numeric" | "tags" | "boolean";

export interface NumericLevel {
  value: number;
  label: string;
  description: string;
}

export interface TagEntry {
  value: string;
  label: string;
  description: string;
}

export interface RubricConfig {
  scoring_type: ScoringType;
  min?: number;
  max?: number;
  levels?: NumericLevel[];
  tags?: TagEntry[];
  pass_description?: string;
  fail_description?: string;
}

function parseConfigToRubric(
  config: Record<string, any> | undefined
): RubricConfig | null {
  if (!config?.scoring_type) return null;
  return config as RubricConfig;
}

function defaultNumericLevels(min: number, max: number): NumericLevel[] {
  return [
    { value: min, label: "", description: "" },
    { value: max, label: "", description: "" },
  ];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (saved: EvaluationCriteria) => void;
  criteria?: EvaluationCriteria | null;
  cloneFrom?: EvaluationCriteria | null;
}

const CriteriaEditModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  criteria,
  cloneFrom,
}) => {
  const { apiKey } = usePermissions();
  const isEditing = !!criteria;

  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [scoringType, setScoringType] = useState<ScoringType>("numeric");
  const [numericMin, setNumericMin] = useState(1);
  const [numericMax, setNumericMax] = useState(5);
  const [levels, setLevels] = useState<NumericLevel[]>(
    defaultNumericLevels(1, 5)
  );
  const [tags, setTags] = useState<TagEntry[]>([
    { value: "", label: "", description: "" },
    { value: "", label: "", description: "" },
  ]);
  const [passDesc, setPassDesc] = useState("");
  const [failDesc, setFailDesc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const source = criteria || cloneFrom;
    if (source) {
      setName(
        cloneFrom ? `${source.name} (Custom)` : source.name
      );
      setInstructions(source.instructions || "");
      const rubric = parseConfigToRubric(
        source.config as Record<string, any>
      );
      if (rubric) {
        setScoringType(rubric.scoring_type);
        setNumericMin(1);
        setNumericMax(5);
        setLevels(defaultNumericLevels(1, 5));
        setTags([
          { value: "", label: "", description: "" },
          { value: "", label: "", description: "" },
        ]);
        setPassDesc("");
        setFailDesc("");
        if (rubric.scoring_type === "numeric") {
          setNumericMin(rubric.min ?? 1);
          setNumericMax(rubric.max ?? 5);
          setLevels(
            rubric.levels?.length
              ? rubric.levels
              : defaultNumericLevels(rubric.min ?? 1, rubric.max ?? 5)
          );
        } else if (rubric.scoring_type === "tags") {
          setTags(
            rubric.tags?.length
              ? rubric.tags
              : [
                  { value: "", label: "", description: "" },
                  { value: "", label: "", description: "" },
                ]
          );
        } else if (rubric.scoring_type === "boolean") {
          setPassDesc(rubric.pass_description || "");
          setFailDesc(rubric.fail_description || "");
        }
      } else {
        setScoringType("numeric");
        setNumericMin(1);
        setNumericMax(5);
        setLevels(defaultNumericLevels(1, 5));
      }
    } else {
      setName("");
      setInstructions("");
      setScoringType("numeric");
      setNumericMin(1);
      setNumericMax(5);
      setLevels(defaultNumericLevels(1, 5));
      setTags([
        { value: "", label: "", description: "" },
        { value: "", label: "", description: "" },
      ]);
      setPassDesc("");
      setFailDesc("");
    }
  }, [open, criteria, cloneFrom]);

  const buildConfig = (): RubricConfig => {
    if (scoringType === "numeric") {
      return {
        scoring_type: "numeric",
        min: numericMin,
        max: numericMax,
        levels: levels.filter((l) => l.label || l.description),
      };
    }
    if (scoringType === "tags") {
      return {
        scoring_type: "tags",
        tags: tags.filter((t) => t.value.trim()),
      };
    }
    return {
      scoring_type: "boolean",
      pass_description: passDesc,
      fail_description: failDesc,
    };
  };

  const isValid = (): boolean => {
    if (!name.trim() || !instructions.trim()) return false;
    if (scoringType === "numeric") {
      return numericMin < numericMax;
    }
    if (scoringType === "tags") {
      return tags.filter((t) => t.value.trim()).length >= 2;
    }
    return true;
  };

  const handleSave = () => {
    if (!isValid()) return;
    setSaving(true);
    const config = buildConfig();
    const payload = {
      name: name.trim(),
      type: EvaluationType.LLM_JUDGE,
      framework: EvaluationFramework.CUSTOM,
      instructions: instructions.trim(),
      config,
    };

    if (isEditing && criteria?.id) {
      EvaluationCriteriaService.update(
        criteria.id,
        { name: payload.name, instructions: payload.instructions, config },
        setSaving,
        apiKey
      ).subscribe(
        (updated: EvaluationCriteria) => {
          toast.success(`Updated "${updated.name}"`);
          onSave(updated);
        },
        (err: any) => {
          console.error("Failed to update criteria", err);
          toast.error("Failed to update criterion");
          setSaving(false);
        }
      );
    } else {
      EvaluationCriteriaService.create(payload, setSaving, apiKey).subscribe(
        (created: EvaluationCriteria) => {
          toast.success(`Created "${created.name}"`);
          onSave(created);
        },
        (err: any) => {
          console.error("Failed to create criteria", err);
          toast.error("Failed to create criterion");
          setSaving(false);
        }
      );
    }
  };

  const updateLevel = (
    idx: number,
    field: keyof NumericLevel,
    val: string | number
  ) => {
    setLevels((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l))
    );
  };

  const addLevel = () => {
    const nextVal =
      levels.length > 0
        ? Math.max(...levels.map((l) => l.value)) + 1
        : numericMin;
    setLevels((prev) => [
      ...prev,
      { value: Math.min(nextVal, numericMax), label: "", description: "" },
    ]);
  };

  const removeLevel = (idx: number) => {
    setLevels((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateTag = (
    idx: number,
    field: keyof TagEntry,
    val: string
  ) => {
    setTags((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        const updated = { ...t, [field]: val };
        if (field === "label" && !t.value) {
          updated.value = val
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_|_$/g, "");
        }
        return updated;
      })
    );
  };

  const addTag = () => {
    setTags((prev) => [...prev, { value: "", label: "", description: "" }]);
  };

  const removeTag = (idx: number) => {
    if (tags.length <= 2) return;
    setTags((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveTag = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    setTags((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-gc-demi-bold">
            {isEditing
              ? "Edit Criterion"
              : cloneFrom
                ? "Clone Criterion"
                : "New Criterion"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the criterion definition and scoring rubric."
              : "Define what the evaluator should assess and how it should score."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="crit-name" className="text-sm font-gc-med">
              Name
            </Label>
            <Input
              id="crit-name"
              placeholder="e.g. Accuracy, Tone Consistency"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Instructions */}
          <div className="space-y-1.5">
            <Label htmlFor="crit-instructions" className="text-sm font-gc-med">
              Instructions
            </Label>
            <p className="text-xs text-muted-foreground font-gc-reg">
              Describe what the evaluator should assess. Do not include scoring
              details here.
            </p>
            <textarea
              id="crit-instructions"
              placeholder="What should the evaluator assess about the response?"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="w-full min-h-[240px] p-2.5 text-sm border border-border rounded-md bg-background resize-y leading-relaxed"
            />
          </div>

          {/* Scoring Type Toggle */}
          <div className="space-y-1.5">
            <Label className="text-sm font-gc-med">Scoring Type</Label>
            <div className="flex gap-1 p-0.5 bg-muted rounded-md w-fit">
              {(
                [
                  ["numeric", "Numeric"],
                  ["tags", "Tags"],
                  ["boolean", "Boolean"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setScoringType(key)}
                  className={`px-3 py-1.5 text-xs rounded transition-colors ${
                    scoringType === key
                      ? "bg-background text-foreground font-gc-med shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Rubric Builder */}
          {scoringType === "numeric" && (
            <NumericRubricBuilder
              min={numericMin}
              max={numericMax}
              levels={levels}
              onMinChange={setNumericMin}
              onMaxChange={setNumericMax}
              onLevelChange={updateLevel}
              onAddLevel={addLevel}
              onRemoveLevel={removeLevel}
            />
          )}

          {scoringType === "tags" && (
            <TagsRubricBuilder
              tags={tags}
              onTagChange={updateTag}
              onAddTag={addTag}
              onRemoveTag={removeTag}
              onMoveTag={moveTag}
            />
          )}

          {scoringType === "boolean" && (
            <BooleanRubricBuilder
              passDesc={passDesc}
              failDesc={failDesc}
              onPassChange={setPassDesc}
              onFailChange={setFailDesc}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid() || saving}>
            {saving ? (
              <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Numeric Rubric Builder ─── */

interface NumericRubricBuilderProps {
  min: number;
  max: number;
  levels: NumericLevel[];
  onMinChange: (v: number) => void;
  onMaxChange: (v: number) => void;
  onLevelChange: (
    idx: number,
    field: keyof NumericLevel,
    val: string | number
  ) => void;
  onAddLevel: () => void;
  onRemoveLevel: (idx: number) => void;
}

const NumericRubricBuilder: React.FC<NumericRubricBuilderProps> = ({
  min,
  max,
  levels,
  onMinChange,
  onMaxChange,
  onLevelChange,
  onAddLevel,
  onRemoveLevel,
}) => (
  <div className="space-y-3">
    <Label className="text-sm font-gc-med">Score Range</Label>
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Min</span>
        <Input
          type="number"
          value={min}
          onChange={(e) => onMinChange(Number(e.target.value))}
          className="w-20 h-8 text-sm"
        />
      </div>
      <span className="text-muted-foreground">–</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Max</span>
        <Input
          type="number"
          value={max}
          onChange={(e) => onMaxChange(Number(e.target.value))}
          className="w-20 h-8 text-sm"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-sm font-gc-med">
        Level Descriptions{" "}
        <span className="font-gc-reg text-muted-foreground">(optional)</span>
      </Label>
      <p className="text-xs text-muted-foreground font-gc-reg">
        Define labels for specific score values. You don't need to cover every
        number.
      </p>
      {levels.map((level, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <Input
            type="number"
            value={level.value}
            onChange={(e) =>
              onLevelChange(idx, "value", Number(e.target.value))
            }
            className="w-16 h-8 text-sm flex-shrink-0"
            min={min}
            max={max}
          />
          <Input
            placeholder="Label"
            value={level.label}
            onChange={(e) => onLevelChange(idx, "label", e.target.value)}
            className="w-28 h-8 text-sm flex-shrink-0"
          />
          <Input
            placeholder="Description for this level"
            value={level.description}
            onChange={(e) =>
              onLevelChange(idx, "description", e.target.value)
            }
            className="h-8 text-sm flex-1"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
            onClick={() => onRemoveLevel(idx)}
          >
            <XIcon className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={onAddLevel}
        className="gap-1.5"
      >
        <PlusIcon className="w-3.5 h-3.5" />
        Add Level
      </Button>
    </div>
  </div>
);

/* ─── Tags Rubric Builder ─── */

interface TagsRubricBuilderProps {
  tags: TagEntry[];
  onTagChange: (idx: number, field: keyof TagEntry, val: string) => void;
  onAddTag: () => void;
  onRemoveTag: (idx: number) => void;
  onMoveTag: (fromIdx: number, toIdx: number) => void;
}

const TagsRubricBuilder: React.FC<TagsRubricBuilderProps> = ({
  tags,
  onTagChange,
  onAddTag,
  onRemoveTag,
  onMoveTag,
}) => {
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [overIdx, setOverIdx] = React.useState<number | null>(null);

  const handleDragStart = (idx: number) => (e: React.DragEvent) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverIdx(idx);
  };

  const handleDrop = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      onMoveTag(dragIdx, idx);
    }
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="space-y-1">
      <div>
        <Label className="text-sm font-gc-med">Tags</Label>
        <p className="text-xs text-muted-foreground font-gc-reg mt-0.5">
          Define the categories the evaluator can pick from. Drag to reorder. At
          least 2 required.
        </p>
      </div>
      <div className="space-y-0">
        {tags.map((tag, idx) => (
          <div key={idx}>
            {overIdx === idx && dragIdx !== null && dragIdx !== idx && (
              <div className="h-0.5 bg-primary rounded-full mx-8 my-1 transition-all" />
            )}
            <div
              draggable
              onDragStart={handleDragStart(idx)}
              onDragOver={handleDragOver(idx)}
              onDrop={handleDrop(idx)}
              onDragEnd={handleDragEnd}
              className={`flex items-start gap-2 py-1.5 rounded-md transition-opacity ${
                dragIdx === idx ? "opacity-30" : ""
              }`}
            >
              <div className="flex items-center h-8 cursor-grab active:cursor-grabbing flex-shrink-0 text-muted-foreground hover:text-foreground">
                <GripVerticalIcon className="w-4 h-4" />
              </div>
              <Input
                placeholder="Label"
                value={tag.label}
                onChange={(e) => onTagChange(idx, "label", e.target.value)}
                className="w-28 h-8 text-sm flex-shrink-0"
              />
              <Input
                placeholder="Description for this tag"
                value={tag.description}
                onChange={(e) => onTagChange(idx, "description", e.target.value)}
                className="h-8 text-sm flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                disabled={tags.length <= 2}
                onClick={() => onRemoveTag(idx)}
              >
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={onAddTag} className="gap-1.5">
        <PlusIcon className="w-3.5 h-3.5" />
        Add Tag
      </Button>
    </div>
  );
};

/* ─── Boolean Rubric Builder ─── */

interface BooleanRubricBuilderProps {
  passDesc: string;
  failDesc: string;
  onPassChange: (v: string) => void;
  onFailChange: (v: string) => void;
}

const BooleanRubricBuilder: React.FC<BooleanRubricBuilderProps> = ({
  passDesc,
  failDesc,
  onPassChange,
  onFailChange,
}) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label className="text-sm font-gc-med">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          What constitutes True?
        </span>
      </Label>
      <textarea
        placeholder="Describe what makes the response evaluate to true…"
        value={passDesc}
        onChange={(e) => onPassChange(e.target.value)}
        className="w-full min-h-[60px] p-2.5 text-sm border border-border rounded-md bg-background resize-y leading-relaxed"
      />
    </div>
    <div className="space-y-1.5">
      <Label className="text-sm font-gc-med">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          What constitutes False?
        </span>
      </Label>
      <textarea
        placeholder="Describe what makes the response evaluate to false…"
        value={failDesc}
        onChange={(e) => onFailChange(e.target.value)}
        className="w-full min-h-[60px] p-2.5 text-sm border border-border rounded-md bg-background resize-y leading-relaxed"
      />
    </div>
  </div>
);

export default CriteriaEditModal;
